import { Action, Mutation, Module, RegisterOptions } from "vuex-class-modules";
import Axios, { AxiosResponse } from "axios";
import { get, set, clear } from "idb-keyval";
import {
    AlarmStatus,
    ILocationFromDatabase,
    ILocationFromParticle,
    IMiscInfo,
} from "@/models/AlarmStatus";
import AxiosSecureModule from "./axiosSecureModule";
import { app, sensor } from "@/store";
import { Log } from "@/plugins/Logging";
import { configureRouter } from "@/router";
import VueRouter from "vue-router";
import useDevice from "@/utilities/useDevice";

@Module({ generateMutationSetters: true })
export default class AlarmModule extends AxiosSecureModule {
    public alarmStatus: AlarmStatus | null = null;
    public armed = false;
    public syncStatus = false;
    public syncAlarm = false;
    public trackingStatus = false;
    public seconds = 0;
    public lastMiscInfo: IMiscInfo | null = null;
    public lastKnownLocation: ILocationFromParticle | null = null;
    public lat: number | null = null;
    public lng: number | null = null;
    public user_lat: number | null = null;
    public user_lng: number | null = null;
    public doorClosed = false;
    public hatchClosed = false;
    public shorePowerOn = false;
    public smartPlugOn = false;
    public digitalIOOn = false;
    public gasValveOpen = false;
    settings: any;
    public gpsRadius = 100;
    public deviceAdded = false;
    public deviceTimedOut = false;
    public inAlarmMode = false;
    public deviceThatTriggeredAlarm = 0;
    public alreadyLoggedIn = false;
    public userLocReady = false;

    constructor(options: RegisterOptions) {
        super(options);
        get("armed").then((value) => {
            if (value) this.setArmed(value);
            else this.setArmed(false);
        });
        get("status").then((value) => {
            if (value) this.SetStatus(value);
        });
    }
    @Action
    public async ClearAlarmData() {
        clear().then(() => {
            this.alarmStatus = null;
        });
    }

    @Action
    public async GetData() {
        await this.defineHeaderAxios();
        Axios.get("api/Alarm")
            .then((result: AxiosResponse) => {
                this.SetData(result.data);
            })
            .catch((error) => {
                Log.error("Get alarm data failed.", error);
            });
    }
    @Action
    public async reset() {
        await this.defineHeaderAxios();
        Axios.post("api/Alarm/reset").catch((error) => {
            Log.error("Reset failed.", error);
            app.error = "Factory reset failed";
        });
    }
    @Action
    public async restart() {
        await this.defineHeaderAxios();
        Axios.post("api/Alarm/restart").catch((error) => {
            Log.error("Restart failed.", error);
            app.error = "Device restart failed";
        });
    }
    @Action
    public async GetStatus() {
        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/status`)
            .then((result: AxiosResponse) => {
                Log.information("Status called.");
            })
            .catch((error) => {
                this.syncAlarm = false;
                Log.error("Status call failed.", error);
            });
    }

    @Action
    public async Arm() {
        this.startSync();

        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/Arm`)
            .then((result: AxiosResponse) => {
                Log.information("Arm called.");
            })
            .catch((error) => {
                this.syncAlarm = false;
                Log.error("Arm failed.", error);
                app.error = "Arming failed";
            });
    }
    startSync() {
        this.syncAlarm = true;
        const checkStatus = () => {
            if (this.syncAlarm) {
                if (this.seconds >= 60) {
                    app.error =
                        "Device not reachable within the last minute. Possibly offline.";
                    this.syncAlarm = false;
                } else {
                    app;
                    this.seconds += 1;
                }
            } else {
                clearInterval(intervalID);
                this.seconds = 0;
            }
        };
        const intervalID = setInterval(checkStatus, 1000);
    }
    startTracking() {
        this.trackingStatus = true;
        Log.information("Tracking started.");
    }

    stopTracking() {
        this.trackingStatus = false;
        Log.information("Tracking stopped.");
    }

    @Action
    public async Disarm() {
        this.startSync();
        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/Disarm`)
            .then((result: AxiosResponse) => {
                Log.information("Disarm called.");
            })
            .catch((error) => {
                Log.error("Disarm failed.", error);
                app.error = "Disarming failed";
                this.syncAlarm = false;
            });
    }
    @Action
    public async getGeoLocation() {
        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/geo`)
            .then((result: AxiosResponse<ILocationFromDatabase>) => {
                Log.information("Fetch geolocation from server");
                if (result.status === 200) {
                    this.lat = result.data.latitude;
                    this.lng = result.data.longitude;
                }
            })
            .catch((error) => {
                Log.error("Fetch geolocation failed.", error);
            });
    }

    @Action
    public async getGeoHistory(dateRange: string) {
        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/geo/${dateRange}`)
            .then((result: AxiosResponse) => {
                if (result.status === 200) {
                    Log.information("Fetch geo history from server");
                }
            })
            .catch((error) => {
                Log.error("Fetch geo history failed.", error);
            });
    }
    @Action
    public async getMiscInfo() {
        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/MiscInfo`)
            .then((result: AxiosResponse) => {
                Log.information("Fetch misc info from server");
            })
            .catch((error) => {
                Log.error("Fetch misc info failed.", error);
            });
    }

    @Action
    public async SetShorePowerState(state: boolean) {
        this.shorePowerOn = state;
        sensor.syncSensor = false;
    }
    @Action
    public async SetSmartPlugState(state: boolean) {
        this.smartPlugOn = state;
        sensor.syncSensor = false;
    }
    @Action
    public async SetDoorState(state: boolean) {
        this.doorClosed = state;
    }
    @Action
    public async SetHatchState(state: boolean) {
        this.hatchClosed = state;
    }
    @Action
    public async SetDigitalIOState(state: boolean) {
        this.digitalIOOn = state;
        sensor.syncSensor = false;
    }
    @Action
    public async SetGasValveState(state: boolean) {
        this.gasValveOpen = state;
        sensor.syncSensor = false;
    }

    @Action
    public async NewDeviceAdded() {
        this.SetNewDeviceAdded(true);
    }
    @Mutation
    public SetNewDeviceAdded(value: boolean) {
        this.deviceAdded = value;
    }
    @Action
    public async NewDeviceTimedOut() {
        this.SetNewDeviceTimedOut(true);
    }
    @Mutation
    public SetNewDeviceTimedOut(value: boolean) {
        this.deviceTimedOut = value;
    }
    @Mutation
    public SetInAlarmMode(value: boolean) {
        this.inAlarmMode = value;
    }

    @Mutation
    public SetAlarmModeTriggered(value: boolean) {
        this.inAlarmMode = value;
        app.alarm = useDevice()
            .getName(this.deviceThatTriggeredAlarm)
            .toUpperCase();
    }
    @Mutation
    public SetAlarmOrigin(device: number) {
        this.deviceThatTriggeredAlarm = device;
    }
    @Mutation
    SetData(data: any) {
        this.settings = data;
    }
    @Mutation
    SetStatus(data: AlarmStatus | null) {
        this.syncStatus = false;
        if (data) {
            this.alarmStatus = data;
            if (data !== null && data.Alarm) {
                this.armed = data.Alarm.trim() === "1";
                this.syncAlarm = false;
                set("armed", this.armed);
            }
            this.alarmStatus = data;
            if (!this.alreadyLoggedIn) {
                this.alreadyLoggedIn = true;
                configureRouter().then((router: VueRouter) => {
                    router.replace({ name: "Home" }).catch(() => {
                        Log.information("Logged in successfuly.");
                    });
                });
            }
        }
    }
    @Mutation
    setArmed(data: any) {
        this.armed = data;
        this.syncAlarm = false;
        set("armed", this.armed);
        if (!this.armed) {
            this.inAlarmMode = false;
        }
    }
    @Mutation
    SetCurrentLocation(location: ILocationFromParticle) {
        this.lastKnownLocation = location;
        this.lat = location.location.geometry.coordinates[1];
        this.lng = location.location.geometry.coordinates[0];
    }
    @Mutation
    SetUserLocation(location: GeolocationPosition) {
        this.user_lat = location.coords.latitude;
        this.user_lng = location.coords.longitude;
        this.userLocReady = true;
    }
    @Mutation
    SetCurentMiscInfo(miscInfo: IMiscInfo) {
        this.lastMiscInfo = miscInfo;
    }
}
